import { Component, ElementRef, Input, Renderer2 } from '@angular/core';
import { BehaviorSubject, Observable, Subject, combineLatest, of } from 'rxjs';
import { format as formatDate } from 'date-fns';
import { Store } from '@ngrx/store';
import { System, isNotNullOrUndefined } from 'ngx-core-services';
import { map, startWith, switchMap, tap } from 'rxjs/operators';

import { appBuildHeatPumpEnergyAggregationMultiBar, appBuildHeatPumpReducedEnergyAggregationCharts } from '../utils/heat-pump-chart.opts';
import { EChartsOption } from 'echarts';

import { CreateCustomBlockSizeType, ICustomBlock } from '../decorators';
import { CreateBlockReduxSupport, IReduxBlock } from '../../store/helpers/decorator';
import {
  SystemsState,
  selectedSystemUid,
  requestSystemHeatpumpEnergyAggregation,
} from '../../store';
import {
  selectHeatpumpEnergyAggregationMonthlyData,
  selectHeatpumpEnergyAggregationMonthlyState,
  selectHeatpumpEnergyAggregationYearlyData,
  selectHeatpumpEnergyAggregationYearlyState
} from '../../store/selectors/heatpump.selectors';

const getLabels = () => ({
  title: 'Energy Aggregation',
  totalEnergy: 'Total Energy',
  totalSessions: 'Total Sessions',
  avgSessionEnergy: 'Avg. Session Energy',
  month: 'Month',
  year: 'Year',
  noDataForTheSelectedPeriod: 'No data available for the selected period',
  energy: 'Energy',
  sessions: 'Sessions',
  date: 'Date',
  HP_E_EG: 'Consumed Energy',
  HP_T_EG: 'Thermal Energy',
  cumulativeConsumedEnergy: 'Cumulative Consumed Energy',
  cumulativeThermalEnergy: 'Cumulative Thermal Energy',
});

interface HeatpumpDailyAggregation {
  date: string;           // YYYY-MM-DD
  HP_E_EG: number;        // kWh
  HP_T_EG: number;        // kWh
  rawData: any | null;    // Original raw object (optional)
}

interface HeatpumpAggregatedEnergyData {
  period: 'month' | 'year';
  startDate: Date;
  endDate: Date;
  HP_E_EG: number;
  HP_T_EG: number;
  dailyData: HeatpumpDailyAggregation[];
  dataloggerType: string;
  hasData: boolean;
}

@CreateBlockReduxSupport()
@CreateCustomBlockSizeType({
  'breakpoint-640': 640,
})
@Component({
  selector: 'lib-heatpump-energy-aggregation',
  templateUrl: './heatpump-energy-aggregation.component.html',
  styleUrls: ['./heatpump-energy-aggregation.component.scss'],
})
export class HeatpumpEnergyAggregationComponent implements ICustomBlock, IReduxBlock {
  @Input() refresh$ = new Subject<void>();
  _refresh$ = new Observable<boolean>();

  readonly _deviceId$ = new BehaviorSubject<string | undefined>(undefined);
  @Input() set deviceUid(value: string | undefined) {
    this._deviceId$.next(value);
  }

  readonly _labels = getLabels();
  @Input() set labels(value: Partial<typeof this._labels>) {
    Object.assign(this._labels, value);
  }
  get labels(): typeof this._labels {
    return this._labels;
  }

  readonly system$ = new BehaviorSubject<System|undefined>(undefined);
  @Input() system?: System;

  readonly _instanceId$: Observable<string> = new Observable<string>();

  readonly periodSelection$ = new BehaviorSubject<'month' | 'year'>('month');
  readonly dateSelection$ = new BehaviorSubject<Date>(new Date());

  readonly requestData$ = combineLatest([
    this._deviceId$.pipe(isNotNullOrUndefined()),
    this.store.select(selectedSystemUid).pipe(isNotNullOrUndefined()),
    this.periodSelection$,
    this.dateSelection$,
    this._refresh$,
    this._instanceId$,
  ]).pipe(
    tap(([deviceId, systemId, period, date, forceRefresh, instanceId]) => {
      this.store.dispatch(requestSystemHeatpumpEnergyAggregation({
        systemId,
        instanceId,
        deviceId,
        period,
        date,
        forceRefresh
      }));
    }),
  );

  readonly monthlyData$ = this._instanceId$.pipe(
      switchMap((instanceId) =>
        combineLatest([
          of(instanceId),
          this._deviceId$.pipe(isNotNullOrUndefined()),
        ])
      ),
      switchMap(([instanceId, deviceId]) =>
        this.store.select(selectHeatpumpEnergyAggregationMonthlyData(instanceId, deviceId))
      ),
    );

    readonly yearlyData$ = this._instanceId$.pipe(
      switchMap((instanceId) =>
        combineLatest([
          of(instanceId),
          this._deviceId$.pipe(isNotNullOrUndefined()),
        ])
      ),
      switchMap(([instanceId, deviceId]) =>
        this.store.select(selectHeatpumpEnergyAggregationYearlyData(instanceId, deviceId))
      ),
    );

    readonly monthlyState$ = this._instanceId$.pipe(
      switchMap((instanceId) =>
        combineLatest([
          of(instanceId),
          this._deviceId$.pipe(isNotNullOrUndefined()),
        ])
      ),
      switchMap(([instanceId, deviceId]) =>
        this.store.select(selectHeatpumpEnergyAggregationMonthlyState(instanceId, deviceId))
      ),
    );

    readonly yearlyState$ = this._instanceId$.pipe(
      switchMap((instanceId) =>
        combineLatest([
          of(instanceId),
          this._deviceId$.pipe(isNotNullOrUndefined()),
        ])
      ),
      switchMap(([instanceId, deviceId]) =>
        this.store.select(selectHeatpumpEnergyAggregationYearlyState(instanceId, deviceId))
      ),
    );

    readonly requestIsPending$ = combineLatest([
      this.periodSelection$,
      this.monthlyState$.pipe(startWith('pending')),
      this.yearlyState$.pipe(startWith('pending')),
    ]).pipe(
      map(([period, monthlyState, yearlyState]) =>
        period === 'month' ? monthlyState === 'pending' : yearlyState === 'pending'
      ),
    );

  readonly aggregatedData$: Observable<HeatpumpAggregatedEnergyData | null> = combineLatest([
    this.periodSelection$,
    this.dateSelection$,
    this.monthlyData$.pipe(startWith(null)),
    this.yearlyData$.pipe(startWith(null))
  ]).pipe(
    map(([period, selectedDate, monthlyData, yearlyData]) => {
      const rawData = period === 'month' ? monthlyData : yearlyData;
      if (!rawData || rawData.length === 0) return null;

      return this.transformToAggregatedData(rawData as any[], period, selectedDate);
    }),
  );


  readonly chartOptions$: Observable<EChartsOption | null> =  combineLatest([
      this.aggregatedData$,
      this.requestIsPending$
    ]).pipe(
      map(([data, isPending]) => {
        if (isPending || !data) return null;
      return appBuildHeatPumpEnergyAggregationMultiBar(
        data,
        this.labels
      );
    })
  );

  readonly reducedChartOptions$: Observable<EChartsOption[] | null> =  combineLatest([
      this.aggregatedData$,
      this.requestIsPending$
    ]).pipe(
      map(([data, isPending]) => {
        if (isPending || !data) return null;
      return appBuildHeatPumpReducedEnergyAggregationCharts(
        data,
        this.labels,
      );
    })
  );

  constructor(
    public readonly el: ElementRef,
    public readonly renderer: Renderer2,
    public readonly store: Store<SystemsState>,
  ) {}

  onPeriodChange(period: any): void {
    this.periodSelection$.next(period as 'month' | 'year');
  }

  onDateSelected(date: string): void {
    this.dateSelection$.next(new Date(date));
  }

  private transformToAggregatedData(rawData: any[], period: 'month' | 'year', selectedDate: Date): HeatpumpAggregatedEnergyData {
    const ATTRS = ['HP_E_EG', 'HP_E_ET', 'HP_T_EG', 'HP_T_ET'] as const;
    type Attr = typeof ATTRS[number];

    const n = (v: any) => {
      const num = Number(v);
      return isFinite(num) ? num : 0;
    };

    const toDateOnly = (item: any): string => {
      const sk: string | undefined = item?.SK;
      if (sk) {
        // Common formats observed: 'DAY#YYYY-MM-DD', 'MONTH#YYYY-MM', etc.
        // 1) If there's a '#', take the substring after the last '#'
        const afterHash = sk.includes('#') ? sk.substring(sk.lastIndexOf('#') + 1) : sk;
        // 2) Try to find an explicit YYYY-MM-DD inside
        const match = afterHash.match(/\d{4}-\d{2}-\d{2}/);
        if (match && match[0]) return match[0];
        // 3) Fallback: if it's YYYY-MM, add '-01'
        const matchMonth = afterHash.match(/\d{4}-\d{2}/);
        if (matchMonth && matchMonth[0]) return `${matchMonth[0]}-01`;
      }
      // Fallback to TS
      const tsRaw = item?.TS;
      const ts = Number(tsRaw);
      const ms = ts > 1e12 ? ts : ts * 1000; // handle sec or ms
      return formatDate(new Date(ms), 'yyyy-MM-dd');
    };

    // 1) Group by exact date (YYYY-MM-DD) and sum energies
    const byDate = new Map<string, HeatpumpDailyAggregation>();
    const ensure = (key: string): HeatpumpDailyAggregation => {
      if (!byDate.has(key)) {
        const dateStr = period === 'year' ? `${key}-01` : key; // month bucket uses YYYY-MM-01
        byDate.set(key, {
          date: dateStr,
          HP_E_EG: 0,
          HP_T_EG: 0,
          rawData: { HP_E_EG: 0, HP_T_EG: 0 },
        });
      }
      return byDate.get(key)!;
    };

    rawData.forEach((row: any) => {
      const date = toDateOnly(row); // YYYY-MM-DD
      const key = period === 'year' ? date.substring(0, 7) : date; // YYYY-MM or YYYY-MM-DD
      const b = ensure(key);
      ATTRS.forEach((attr: Attr) => {
        const val = n(row?.[attr]);
        (b as any)[attr] += val;
        (b.rawData as any)[attr] = (b as any)[attr]; // keep rawData in sync with aggregated sums
      });
    });

    // 2) Compute start/end window from selectedDate (month/year)
    let startDate: Date;
    let endDate: Date;
    if (period === 'month') {
      startDate = new Date(selectedDate.getFullYear(), selectedDate.getMonth(), 1);
      endDate = new Date(selectedDate.getFullYear(), selectedDate.getMonth() + 1, 0);
    } else {
      startDate = new Date(selectedDate.getFullYear(), 0, 1);
      endDate = new Date(selectedDate.getFullYear(), 11, 31);
    }

    // 3) Build the full date list and fill missing with zeros
    const allKeys: string[] = [];
    if (period === 'year') {
      const d = new Date(startDate.getFullYear(), 0, 1);
      while (d <= endDate) {
        allKeys.push(formatDate(d, 'yyyy-MM'));
        d.setMonth(d.getMonth() + 1);
      }
    } else {
      for (let d = new Date(startDate); d <= endDate; d.setDate(d.getDate() + 1)) {
        allKeys.push(formatDate(d, 'yyyy-MM-dd'));
      }
    }

    const dailyData: HeatpumpDailyAggregation[] = allKeys.map(key => {
      const bucket = byDate.get(key);
      if (bucket) return bucket;
      // missing bucket -> zeroed
      return {
        date: period === 'year' ? `${key}-01` : key,
        HP_E_EG: 0,
        HP_T_EG: 0,
        rawData: { HP_E_EG: 0, HP_T_EG: 0 },
      };
    });

    // 4) Totali del periodo per chiave
    const totals = dailyData.reduce(
      (acc, d) => {
        acc.HP_E_EG += d.HP_E_EG;
        acc.HP_T_EG += d.HP_T_EG;
        return acc;
      },
      { HP_E_EG: 0, HP_T_EG: 0 }
    );

    return {
      period,
      startDate,
      endDate,
      HP_E_EG: totals.HP_E_EG,
      HP_T_EG: totals.HP_T_EG,
      dailyData,
      dataloggerType: (rawData[0] && rawData[0].__typename) || 'HeatPumpDAY',
      hasData: dailyData.some(d => (d.HP_E_EG + d.HP_T_EG) > 0),
    };
  }
}
